import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articleDetails } from '@/mocks/article';
import { Helmet } from '@/pages/home/components/Helmet';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articleDetails[Number(id)] : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h1>
            <p className="mt-2 text-foreground-600">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryMap: Record<string, string> = {
    meizhuang: '国产美妆',
    shuma: '国产数码',
    shipin: '国产食品',
    fushi: '国产服饰',
  };

  return (
    <>
      <Helmet
        title={`${article.title} | 国产专区`}
        description={article.content.find((c) => c.type === 'paragraph')?.text?.substring(0, 150) || article.title}
        keywords={`${article.tags.join(',')},国产专区,国货推荐`}
        canonicalUrl={`https://www.guochanzhuanqu.cn/article/${article.id}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          'headline': article.title,
          'description': article.content.find((c) => c.type === 'paragraph')?.text?.substring(0, 150) || '',
          'datePublished': article.date,
          'image': article.image,
          'author': { '@type': 'Organization', 'name': '国产专区' },
          'publisher': { '@type': 'Organization', 'name': '国产专区', 'url': 'https://www.guochanzhuanqu.cn/' },
          'url': `https://www.guochanzhuanqu.cn/article/${article.id}`,
          'inLanguage': 'zh-CN',
          'breadcrumb': {
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.guochanzhuanqu.cn/' },
              { '@type': 'ListItem', 'position': 2, 'name': categoryMap[article.categorySlug] || article.category, 'item': `https://www.guochanzhuanqu.cn/category?tab=${article.categorySlug}` },
              { '@type': 'ListItem', 'position': 3, 'name': article.title, 'item': `https://www.guochanzhuanqu.cn/article/${article.id}` },
            ],
          },
        }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryMap[article.categorySlug] || article.category, href: `/category?tab=${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <header className="mb-8">
            <span className="inline-block bg-accent-500 text-white text-xs px-3 py-1 rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
              <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
              <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
              <span><i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()} 阅读</span>
            </div>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-[300px] md:h-[400px] object-cover"
            />
          </div>

          <div className="prose-custom">
            {article.content.map((block, index) => {
              if (block.type === 'heading' && block.text) {
                return (
                  <h2 key={index} className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-8 mb-4">
                    {block.text}
                  </h2>
                );
              }
              if (block.type === 'paragraph' && block.text) {
                return (
                  <p key={index} className="text-base text-foreground-700 leading-relaxed mb-4">
                    {block.text}
                  </p>
                );
              }
              return null;
            })}
          </div>

          <div className="mt-8 pt-6 border-t border-background-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-600">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block bg-secondary-100 text-secondary-700 text-xs px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {article.relatedArticles && article.relatedArticles.length > 0 && (
            <section className="mt-10 pt-6 border-t border-background-200" aria-labelledby="related-title">
              <h2 id="related-title" className="text-xl font-heading font-bold text-foreground-900 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {article.relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group flex gap-4 bg-background-100 rounded-lg p-3 cursor-pointer transition-all hover:scale-[1.01]"
                  >
                    <div className="w-24 h-20 flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-heading font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}